/**
 * What an invoice's state means for the screen: its chip, its age line, its
 * one primary action and the tax label. Pure functions so the invoice drawer,
 * the payment sheet and tests all agree.
 *
 * Money definitions follow DECISIONS §8 (same as Home / Reports):
 *   - Money owed = balance due on ISSUED invoices (Sent, Viewed, Partly paid,
 *     Overdue). Drafts and voided invoices are excluded.
 *   - Overdue = owed AND past its due date — date-only, in the workspace time
 *     zone. Due today is not overdue. The stored status is not consulted
 *     (an invoice is late the day after it is due even before the hourly
 *     sweep flips its status).
 */
import type { WorkStatus } from '@/components/ui/Chip'
import { formatDateOnly, toDateOnly, todayDateOnly } from '@/lib/dateOnly'
import { formatPercent } from '@/lib/format'

export type InvoiceStatus = 'DRAFT' | 'SENT' | 'VIEWED' | 'PAID' | 'PARTIALLY_PAID' | 'OVERDUE' | 'VOIDED' | 'WRITTEN_OFF' | 'CANCELLED'

export const ISSUED_UNPAID: InvoiceStatus[] = ['SENT', 'VIEWED', 'PARTIALLY_PAID', 'OVERDUE']
export const CLOSED: InvoiceStatus[] = ['VOIDED', 'WRITTEN_OFF', 'CANCELLED']

/**
 * Phase colours (lib/phaseTone): a draft isn't real money yet (neutral), an
 * open bill is billing butter, paid is mint, late is blush, closed is neutral.
 */
export const INVOICE_CHIP: Record<InvoiceStatus, WorkStatus> = {
  DRAFT: 'archived',
  SENT: 'invoiced',
  VIEWED: 'invoiced',
  PARTIALLY_PAID: 'invoiced',
  PAID: 'paid',
  OVERDUE: 'overdue',
  VOIDED: 'archived',
  WRITTEN_OFF: 'archived',
  CANCELLED: 'cancelled',
}

type Billable = { status: string; total: number; amountPaid: number; dueDate: string; sentAt?: string | null }

/** Balance due in cents (never negative). */
export function balanceCents(inv: Pick<Billable, 'total' | 'amountPaid'>): number {
  return Math.max(0, Math.round(inv.total * 100) - Math.round(inv.amountPaid * 100))
}

export function isOwed(inv: Pick<Billable, 'status' | 'total' | 'amountPaid'>): boolean {
  return ISSUED_UNPAID.includes(inv.status as InvoiceStatus) && balanceCents(inv) > 0
}

/** Whole days past the due date (0 when not yet late). */
export function daysOverdue(dueDate: string | null | undefined, tz?: string | null, now?: Date): number {
  const due = toDateOnly(dueDate, tz)
  if (!due) return 0
  const today = todayDateOnly(tz, now)
  const ms = Date.parse(`${today}T00:00:00Z`) - Date.parse(`${due}T00:00:00Z`)
  return ms > 0 ? Math.round(ms / 86_400_000) : 0
}

export function isOverdue(inv: Billable, tz?: string | null, now?: Date): boolean {
  return isOwed(inv) && daysOverdue(inv.dueDate, tz, now) > 0
}

/**
 * The one line that says where the bill stands:
 *   "12 days overdue · sent Oct 3", "Due Nov 2 · sent Oct 3", "Due today",
 *   "Draft · not sent yet", "Paid Oct 20".
 */
export function invoiceAgeLine(inv: Billable & { paidAt?: string | null }, tz?: string | null, now?: Date): string {
  const sent = inv.sentAt ? `sent ${formatDateOnly(inv.sentAt, 'short', tz)}` : null
  if (inv.status === 'DRAFT') return 'Draft · not sent yet'
  if (inv.status === 'PAID') return inv.paidAt ? `Paid ${formatDateOnly(inv.paidAt, 'short', tz)}` : 'Paid'
  if (CLOSED.includes(inv.status as InvoiceStatus)) return ''
  const late = daysOverdue(inv.dueDate, tz, now)
  const head = late > 0
    ? `${late} ${late === 1 ? 'day' : 'days'} overdue`
    : toDateOnly(inv.dueDate, tz) === todayDateOnly(tz, now)
      ? 'Due today'
      : `Due ${formatDateOnly(inv.dueDate, 'short', tz)}`
  return [head, sent].filter(Boolean).join(' · ')
}

export type InvoicePrimaryAction = 'send' | 'remind' | 'pay' | 'receipt' | null

/**
 * One primary action per state (UX-068, UX-045):
 *   draft → Send invoice · open → Take payment · overdue → Send reminder ·
 *   paid → Send receipt · voided/cancelled → nothing.
 */
export function primaryAction(inv: Billable, tz?: string | null, now?: Date): InvoicePrimaryAction {
  if (inv.status === 'DRAFT') return 'send'
  if (inv.status === 'PAID') return 'receipt'
  if (CLOSED.includes(inv.status as InvoiceStatus)) return null
  if (balanceCents(inv) === 0) return 'receipt'
  return isOverdue(inv, tz, now) ? 'remind' : 'pay'
}

/** "Tax (7%)" — the rate is shown wherever tax is (UX-211). */
export function taxLabel(taxRate: number | null | undefined): string {
  if (taxRate == null || !Number.isFinite(taxRate) || taxRate <= 0) return 'Tax'
  return `Tax (${formatPercent(taxRate * 100, { maxDecimals: 3 })})`
}
